import {
  decodeFilterByIdList,
  filterByIdListWithUnknownPayload,
} from "@/domain/view/components/datatable/datatable-filters/functions/filter-by-id-list";
import { DatatableColumnHeaderCell } from "@/domain/view/components/datatable/datatable-template-cells/datatable-column-header-cell";
import {
  DatatableColumn,
  DatatableRowData,
  isRawRow,
  isSectionRow,
  isSubSectionRow,
} from "@/domain/view/components/datatable/datatable-types";
import { Workspace } from "@/domain/workspace/models/workspace-model";
import { compareFractionalOrder } from "@/lib/compare-fractional-order";

import { FocusAreaInstance } from "../../models/focus-area-model";

import { DatatableFocusAreaAppliedFilterView } from "./datatable-focus-area-applied-filter-view";
import { DatatableFocusAreaCell } from "./datatable-focus-area-cell";
import { DatatableFocusAreaConfigurationView } from "./datatable-focus-area-configuration-view";
import { DatatableFocusAreaDisplayView } from "./datatable-focus-area-display-view";
import { DatatableFocusAreaSectionHeader } from "./datatable-focus-area-section-header";
import { DatatableFocusAreaSubSectionHeader } from "./datatable-focus-area-subsection-header";

export interface DatatableFocusAreaTemplateColumnProps<TData extends DatatableRowData> {
  workspace: Workspace;
  columnId?: string;
  orderValue?: number;
  displayValue?: string;
  editable?: boolean;
  focusAreaInstanceFromRow: (row: TData) => FocusAreaInstance | null | undefined;
  ownerIdFromRow: (row: TData) => string;
}

export const DatatableFocusAreaTemplateColumn = <TData extends DatatableRowData>({
  workspace,
  columnId = "focus_area",
  orderValue = 101,
  displayValue = "Handlungsfeld", // default; callers should pass translated value
  editable = false,
  focusAreaInstanceFromRow,
  ownerIdFromRow,
}: DatatableFocusAreaTemplateColumnProps<TData>): DatatableColumn<TData> => {
  return {
    columnId: columnId,
    getDefaultVisibilityState: () => true,
    getDisplayValue: () => displayValue,
    getOrderValue: () => orderValue,
    enableSectioning: true,
    getSectionKey: (data) => focusAreaInstanceFromRow(data)?.focusAreaId ?? "",
    getColumnDef: () => {
      return {
        id: columnId,
        accessorKey: columnId,
        enableHiding: true,
        meta: { enableDragging: true },
        enableSorting: true,
        enableColumnFilter: true,
        size: 1,
        sortingFn: (rowA, rowB) => {
          if (!isRawRow(rowA.original)) {
            return 0;
          }

          if (!isRawRow(rowB.original)) {
            return 0;
          }

          const focusAreaA = focusAreaInstanceFromRow(rowA.original);
          const focusAreaB = focusAreaInstanceFromRow(rowB.original);

          return compareFractionalOrder(focusAreaA?.focusArea?.order, focusAreaB?.focusArea?.order);
        },
        filterFn: (row, _columnId, value, _addMeta) => {
          if (!isRawRow(row.original)) {
            return true;
          }

          const focusAreaInstance = focusAreaInstanceFromRow(row.original);
          return filterByIdListWithUnknownPayload(value, focusAreaInstance?.focusAreaId);
        },
        header: ({ column }) => (
          <DatatableColumnHeaderCell column={column} className="text-xs font-normal">
            {displayValue}
          </DatatableColumnHeaderCell>
        ),
        cell: ({ row }) => {
          if (!isRawRow(row.original)) {
            return null;
          }

          const focusArea = focusAreaInstanceFromRow(row.original);
          const ownerId = ownerIdFromRow(row.original);
          return (
            <DatatableFocusAreaCell
              focusAreaInstance={focusArea ?? null}
              workspaceId={workspace.id}
              ownerId={ownerId}
              editable={editable}
            />
          );
        },
      };
    },
    renderDisplayView: () => <DatatableFocusAreaDisplayView displayValue={displayValue} />,
    getFilterConfigurationViewType: () => "popover",
    renderFilterConfigurationView: (onFinished: () => void) => (
      <DatatableFocusAreaConfigurationView workspaceId={workspace.id} columnId={columnId} onFinished={onFinished} />
    ),
    renderAppliedFilterView: () => (
      <DatatableFocusAreaAppliedFilterView workspaceId={workspace.id} columnId={columnId} displayValue={displayValue} />
    ),
    renderSectionHeader: (data, row) => {
      return <DatatableFocusAreaSectionHeader data={data} currentRow={row} />;
    },
    renderSubSectionHeader: (data, row) => {
      return <DatatableFocusAreaSubSectionHeader data={data} currentRow={row} />;
    },
    sortSection: (a, b) => {
      if (!a.key || !b.key) {
        return 0;
      }
      if (!isSectionRow(a) || !isSectionRow(b)) {
        return 0;
      }

      const firstEntryA = a.data[0];
      const firstEntryB = b.data[0];

      if (isSubSectionRow(firstEntryA) && isSubSectionRow(firstEntryB)) {
        const focusAreaA = focusAreaInstanceFromRow(firstEntryA.data[0]);
        const focusAreaB = focusAreaInstanceFromRow(firstEntryB.data[0]);
        return compareFractionalOrder(focusAreaA?.focusArea?.order, focusAreaB?.focusArea?.order);
      }

      if (isRawRow(firstEntryA) && isRawRow(firstEntryB)) {
        const focusAreaA = focusAreaInstanceFromRow(firstEntryA);
        const focusAreaB = focusAreaInstanceFromRow(firstEntryB);
        return compareFractionalOrder(focusAreaA?.focusArea?.order, focusAreaB?.focusArea?.order);
      }

      return 0;
    },
    sortSubSection: (a, b) => {
      if (!a.key || !b.key) {
        return 0;
      }
      if (!isSubSectionRow(a) || !isSubSectionRow(b)) {
        return 0;
      }

      const firstEntryA = a.data[0];
      const firstEntryB = b.data[0];

      if (isRawRow(firstEntryA) && isRawRow(firstEntryB)) {
        const focusAreaA = focusAreaInstanceFromRow(firstEntryA);
        const focusAreaB = focusAreaInstanceFromRow(firstEntryB);
        return compareFractionalOrder(focusAreaA?.focusArea?.order, focusAreaB?.focusArea?.order);
      }

      return 0;
    },
    resolveStateFromUrlParams: (urlParams: URLSearchParams) => {
      const filter = decodeFilterByIdList(columnId, urlParams);
      if (filter) {
        return {
          columnFilters: [
            {
              id: columnId,
              value: filter,
            },
          ],
        };
      }
      return undefined;
    },
  };
};
